/**
 * AudioVideoTab: the "Audio and video" tab of the "Me" dialog.
 *
 * Camera/mic selection, flattened from the standalone DeviceSelector dropdown
 * (apps/web/src/components/mateList/controls/DeviceSelector) into plain inline
 * lists, since this is already inside a modal - no need for a nested popover.
 */
import { cn } from '@merels/ui'
import type { MediaDeviceOption } from '../../../controls/DeviceSelector/DeviceSelector'
import { VideoconferenceToggle } from '../../../../library/VideoconferenceToggle/VideoconferenceToggle'

export interface AudioVideoTabProps {
  /** Whether Daily.co videoconferencing is enabled for the current room. */
  videoEnabled: boolean
  /** Whether the current user owns this room - lets them turn videoconferencing on directly. */
  isOwner: boolean
  onToggleVideoEnabled: (next: boolean) => Promise<void>
  cameras: MediaDeviceOption[]
  mics: MediaDeviceOption[]
  selectedCameraId: string | null
  selectedMicId: string | null
  onSelectCamera: (deviceId: string) => void
  onSelectMic: (deviceId: string) => void
  /**
   * Flat-list controller navigation wiring (see NameDialogUI) - all optional so this
   * component still works standalone (tests) without a parent nav list. Indices are
   * local (start at 0); NameDialogUI offsets them by baseIndex before storing.
   */
  registerItem?: (index: number, el: HTMLElement | null) => void
  focusedIndex?: number
  baseIndex?: number
}

function DeviceList({
  devices,
  selectedId,
  onSelect,
  registerItem,
  isItemFocused,
  startIndex,
}: {
  devices: MediaDeviceOption[]
  selectedId: string | null
  onSelect: (deviceId: string) => void
  registerItem?: (index: number, el: HTMLElement | null) => void
  isItemFocused: (localIndex: number) => boolean
  startIndex: number
}) {
  return (
    <div className="flex flex-col gap-1">
      {devices.map((d, i) => {
        const localIndex = startIndex + i
        return (
          <button
            key={d.deviceId}
            ref={(el) => registerItem?.(localIndex, el)}
            type="button"
            onClick={() => onSelect(d.deviceId)}
            className={cn(
              'text-left text-sm px-2 py-1.5 rounded cursor-pointer transition-colors focus:outline-none',
              d.deviceId === selectedId
                ? 'bg-accent text-inverse font-semibold'
                : 'text-primary hover:bg-subtle',
              isItemFocused(localIndex) && 'ring-2 ring-accent'
            )}
          >
            {d.label}
          </button>
        )
      })}
    </div>
  )
}

export function AudioVideoTab({
  videoEnabled,
  isOwner,
  onToggleVideoEnabled,
  cameras,
  mics,
  selectedCameraId,
  selectedMicId,
  onSelectCamera,
  onSelectMic,
  registerItem,
  focusedIndex,
  baseIndex = 0,
}: AudioVideoTabProps) {
  const isItemFocused = (localIndex: number) => registerItem !== undefined && focusedIndex === baseIndex + localIndex

  if (!videoEnabled) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          {isOwner
            ? 'Videoconferencing is turned off for this room.'
            : 'Videoconferencing is turned off for this room. Ask the room owner to turn it on.'}
        </p>
        {isOwner && (
          <VideoconferenceToggle
            ref={(el) => registerItem?.(baseIndex, el)}
            videoEnabled={false}
            onToggle={onToggleVideoEnabled}
            testId="audio-video-tab-videoconference-toggle"
            className={cn(isItemFocused(0) && 'ring-2 ring-accent rounded')}
          />
        )}
      </div>
    )
  }

  if (cameras.length === 0 && mics.length === 0) {
    return <p className="text-sm text-muted">No camera or microphone found.</p>
  }

  return (
    <div className="flex flex-col gap-4">
      {mics.length > 0 && (
        <div>
          <div className="text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Microphone</div>
          <DeviceList
            devices={mics}
            selectedId={selectedMicId}
            onSelect={onSelectMic}
            registerItem={registerItem ? (i, el) => registerItem(baseIndex + i, el) : undefined}
            isItemFocused={isItemFocused}
            startIndex={0}
          />
        </div>
      )}
      {cameras.length > 0 && (
        <div>
          <div className="text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Camera</div>
          <DeviceList
            devices={cameras}
            selectedId={selectedCameraId}
            onSelect={onSelectCamera}
            registerItem={registerItem ? (i, el) => registerItem(baseIndex + i, el) : undefined}
            isItemFocused={isItemFocused}
            startIndex={mics.length}
          />
        </div>
      )}
    </div>
  )
}
